import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Box,
  CircularProgress,
  Paper,
  Radio,
  Stack,
  Typography,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import {
  UseFormWatch,
  UseFormSetValue,
  FieldErrors,
  Control,
  UseFormClearErrors,
} from "react-hook-form"
import React from "react"
import CreditCardPaymentPrice from "./CreditCard"
import DebitCardPaymentPrice from "./DebitCard"
import PixPaymentPrice from "./Pix"
import BoletoPaymentPrice from "./Boleto"
import {
  IProductPrice,
  IProductPricePayment,
  IProductPricePaymentInstallment,
  TPaymentMethodEnum,
} from "../../../../../core/types/Product"
import { TSale } from "../../../../../core/types/Sale"
import { checkoutPayment6Style } from "./PaymentConsts"
import { PaymentData } from "../../../../../core/types/Order"
import Promises from "../../../../../core/functions/promises"
import { grey } from "@mui/material/colors"
import PaymentDeliveryBadge from "./PaymentDeliveryBadge"
import { useCheckoutContext } from "../../../../../core/hooks/useCheckoutContext"
import { useGatewayContext } from "../../../../../core/contexts/GatewayContext"

export interface IPaymentsByProduct6Props {
  loading: boolean
  data: IProductPrice | null
  control: Control<TSale, any>
  watch: UseFormWatch<TSale>
  setValue: UseFormSetValue<TSale>
  errors: FieldErrors<TSale>
  installments?: IProductPricePaymentInstallment[]
  clearErrors: UseFormClearErrors<TSale>
}

/**
 * Componente responsável por gerenciar as formas de pagamentos no checkout.
 */
const PaymentsByProduct6 = (props: IPaymentsByProduct6Props) => {
  const {
    loading,
    data,
    setValue,
    control,
    errors,
    watch,
    installments,
    clearErrors,
  } = props

  const { isPurposeCardUpdate } = useCheckoutContext()
  const { useStripe: useStripePayment } = useGatewayContext()

  const paymentMethods =
    (
      (isPurposeCardUpdate()
        ? data?.paymentMethods.filter(
            (pm) => pm.methodType === TPaymentMethodEnum.CreditCard
          )
        : data?.paymentMethods.filter(
            (pm) => ![TPaymentMethodEnum.CreditCardAndPix].includes(pm.methodType)
          )
      )?.filter(
        (pm) => !useStripePayment || pm.methodType === TPaymentMethodEnum.CreditCard
      )
    ) ?? []

  const maxPaymentLine = 4
  const isMorePayments = (paymentMethods.length ?? 0) > maxPaymentLine

  const [selected, setSelected] = React.useState(0)
  const [openMorePay, setOpenMorePay] = React.useState(!isMorePayments)
  const [isPaymentVisible, setIsPaymentVisible] = React.useState(true)

  const paymentMethodType = watch("payment.methodType")
  const payments = watch("payment.payments")

  React.useEffect(() => {
    if (paymentMethodType !== selected) {
      setSelected(paymentMethodType)
    }
  }, [paymentMethodType, selected])

  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: TPaymentMethodEnum | null | undefined
  ) => {
    if (!newValue) {
      setOpenMorePay(!openMorePay)
      return
    }
    const isChanged = selected !== newValue
    try {
      if (isChanged) {
        setValue("payment.payments", [])
        setIsPaymentVisible(false)
      }

      setSelected(newValue)
      setValue("payment.methodType", newValue)

      if (isChanged) {
        await Promises.delay(200)
      }
    } finally {
      if (isChanged) {
        setIsPaymentVisible(true)
      }
    }
  }

  const getPaymentMethods = () => {
    return isMorePayments && !openMorePay
      ? paymentMethods.filter((pm, i) => i < maxPaymentLine - 1)
      : paymentMethods
  }

  const getPaymentDescription = (payment: IProductPricePayment | undefined) => {
    if (!payment) {
      return openMorePay ? "Ver menos" : "Ver mais"
    }
    return checkoutPayment6Style[payment.methodType].label
  }

  const getPaymentItem = (
    payment: IProductPricePayment | undefined,
    index: number
  ) => {
    const testId = `buttonPayment${Number(
      payment?.methodType ?? TPaymentMethodEnum.CreditCard
    )}`

    return (
      <Paper
        key={`pay-${index}`}
        elevation={0}
        sx={{
          width: "100%",
          p: 0.5,
          border: "1px solid #283b7b",
          borderRadius: "10px",
        }}
      >
        <Accordion
          elevation={0}
          data-testid={testId}
          data-cy={testId}
          sx={{ width: "100%", borderRadius: "12px" }}
          expanded={paymentMethodType === payment?.methodType}
          onChange={(e) => handleChange(e, payment?.methodType)}
        >
          <AccordionSummary
            sx={{
              width: "100%",
              p: "0px",
              margin: "0px",
              //pl: 1,
              //pr: 2,
              [`& .${accordionSummaryClasses.content}`]: {
                margin: "0px",
              },
            }}
          >
            <Stack
              direction="row"
              width="100%"
              spacing={0.5}
              alignItems="center"
              sx={{ mx: { xs: 0, sm: 2 } }}
            >
              <Radio
                size="small"
                checked={paymentMethodType === payment?.methodType}
                sx={{
                  m: "0px",
                  p: "0px",
                  color: grey[800],
                  "&.Mui-checked": {
                    color: grey[700],
                  },
                }}
              />
              <Typography
                fontSize="14px"
                lineHeight="14px"
                fontWeight={700}
                sx={{ flexGrow: 1 }}
              >
                {getPaymentDescription(payment)}
              </Typography>
              <PaymentDeliveryBadge />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Stack sx={{ width: "100%" }} spacing={3}>
                {payments.map(getPaymentWidget)}
                {payments.length <= 0 && <Box height="300px" />}
              </Stack>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>
    )
  }

  const getPaymentWidget = (pay: PaymentData, index: number) => {
    const key = `${paymentMethodType.toString()}-${pay.paymentMethod.toString()}`
    if (pay.paymentMethod === TPaymentMethodEnum.CreditCard) {
      const title = payments.length > 1 ? `${index + 1}º Cartão` : undefined
      return (
        <CreditCardPaymentPrice
          key={key}
          visible={true}
          title={title}
          control={control}
          errors={errors}
          installments={installments}
          index={index}
          isMultiple={payments.length > 1}
          watch={watch}
          setValue={setValue}
          clearErrors={clearErrors}
        />
      )
    }
    if (pay.paymentMethod === TPaymentMethodEnum.DebitCard) {
      return (
        <DebitCardPaymentPrice
          key={key}
          visible={true}
          control={control}
          index={index}
          watch={watch}
          clearErrors={clearErrors}
        />
      )
    }
    if (pay.paymentMethod === TPaymentMethodEnum.Pix) {
      const title = payments.length > 1 ? "PIX" : undefined
      return (
        <PixPaymentPrice
          key={key}
          visible={true}
          title={title}
          index={index}
          isMultiple={payments.length > 1}
          watch={watch}
          setValue={setValue}
        />
      )
    }
    if (pay.paymentMethod === TPaymentMethodEnum.Boleto) {
      return (
        <BoletoPaymentPrice
          key={key}
          visible={true}
          index={index}
          watch={watch}
        />
      )
    }
    return <></>
  }

  return (
    <Stack sx={{ width: "100%" }}>
      {loading && <CircularProgress />}
      {!loading && data !== null && (
        <Stack direction="column" sx={{ width: "100%" }} spacing={3}>
          {getPaymentMethods().map((payment, index) =>
            getPaymentItem(payment, index)
          )}
          {isMorePayments && getPaymentItem(undefined, -1)}
        </Stack>
      )}
    </Stack>
  )
}

export default PaymentsByProduct6
